import React, { useState } from 'react';
import { Modal, Header, Form, Input, Button, Message, Icon, Dimmer, Loader } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { simulationService } from '../../services/simulation/SimulationService';
import { useToast } from '../../hooks/useToast';
import { SimulationClientDto } from '../../types/simulation.types';
import SimulationModal from './SimulationModal';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SimulationSearchModal: React.FC<Props> = ({ open, onClose }) => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [cliente, setCliente] = useState<SimulationClientDto | null>(null);
  const [simulationModalOpen, setSimulationModalOpen] = useState(false);

  const normalizeCnpj = (cnpj: string): string => {
    return cnpj.replace(/[^\d]/g, '');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showError('Por favor, informe um CNPJ ou Razão Social');
      return;
    }

    setLoading(true);
    try {
      const normalizedQuery = normalizeCnpj(searchQuery);
      
      // Buscar diretamente na API (removida a validação do n8n/Sheets)
      const clienteEncontrado = await simulationService.searchClientByCnpjOrName(
        normalizedQuery.length === 14 ? normalizedQuery : searchQuery.trim()
      );
      
      setCliente(clienteEncontrado);
      setSimulationModalOpen(true);
      setSearchQuery('');
    } catch (error: any) {
      // Cliente não encontrado
      showError(error.message || 'Cliente não encontrado');
      
      // Perguntar se deseja criar contrato manualmente
      const criarManual = window.confirm(
        'Cliente não encontrado na base. Deseja abrir o modal de criação de contrato para cadastrar manualmente?'
      );
      
      if (criarManual) {
        // Fechar modal de busca
        handleClose();
        // Navegar e abrir modal vazio
        navigate('/contracts');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openContractModal'));
        }, 100);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setCliente(null);
    onClose();
  };

  const handleSimulationClose = () => {
    setSimulationModalOpen(false);
    setCliente(null);
    handleClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        size="small"
        closeIcon
        style={{
          maxWidth: '600px',
        }}
      >
        <Modal.Header style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px 24px',
          borderBottom: 'none'
        }}>
          <Header as="h2" style={{ color: 'white', margin: 0 }}>
            <Icon name="calculator" style={{ color: 'white' }} />
            Buscar Cliente para Simulação
          </Header>
        </Modal.Header>

        <Modal.Content style={{ padding: '24px', backgroundColor: '#f8f9fa' }}>
          <Dimmer active={loading} inverted>
            <Loader content="Buscando cliente..." />
          </Dimmer>

          <Form>
            <Form.Field>
              <label style={{ 
                color: '#2c3e50', 
                marginBottom: '10px',
                fontWeight: 600,
                fontSize: '14px'
              }}>
                CNPJ ou Razão Social
              </label>
              <Input
                placeholder="Digite o CNPJ ou Razão Social do cliente"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                icon="search"
                iconPosition="left"
                fluid
                size="large"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                }}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <small style={{ 
                color: '#6c757d', 
                marginTop: '8px', 
                display: 'block',
                fontSize: '12px'
              }}>
                A busca será normalizada automaticamente (removendo espaços e caracteres especiais)
              </small>
            </Form.Field>
          </Form>

          <Message 
            info 
            style={{ 
              marginTop: '24px',
              borderRadius: '8px',
              backgroundColor: '#e3f2fd',
              borderLeft: '4px solid #2196f3',
            }}
          >
            <Message.Header style={{ color: '#1976d2', fontSize: '14px', fontWeight: 600 }}>
              <Icon name="info circle" />
              Como usar
            </Message.Header>
            <Message.List style={{ marginTop: '12px', fontSize: '13px' }}>
              <Message.Item>Digite o CNPJ completo (14 dígitos) ou Razão Social</Message.Item>
              <Message.Item>O sistema normalizará automaticamente o CNPJ</Message.Item>
              <Message.Item>Após encontrar o cliente, o modal de simulação será aberto</Message.Item>
            </Message.List>
          </Message>
        </Modal.Content>

        <Modal.Actions style={{ 
          padding: '16px 24px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 500,
            }}
          >
            Cancelar
          </Button>
          <Button
            primary
            onClick={handleSearch}
            loading={loading}
            disabled={loading || !searchQuery.trim()}
            style={{
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Icon name="search" />
            Buscar
          </Button>
        </Modal.Actions>
      </Modal>

      {cliente && (
        <SimulationModal
          open={simulationModalOpen}
          onClose={handleSimulationClose}
          clienteId={cliente.id}
        />
      )}
    </>
  );
};

export default SimulationSearchModal;

